import { Component } from "react";

const LTEProductID = 10300;
const WIFIProductID = 18278;

export class ParticleService extends Component {
    static LTEProductID = LTEProductID;
    static WIFIProductID = WIFIProductID;
    static csrfToken = null;
    static adminFleetPromise = null;
    static adminFleetLoadedAt = 0;

    static async getSession() {
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), 15000);

        try {
            const response = await fetch('/api/auth/session', {
                credentials: 'same-origin',
                signal: controller.signal,
            });
            if (!response.ok) return { authenticated: false };
            const session = await response.json();
            if (session.csrfToken) this.csrfToken = session.csrfToken;
            if (session.authenticated && session.email) localStorage.setItem('currentUser', session.email);
            return session;
        } finally {
            clearTimeout(timeout);
        }
    }

    static async getCsrfToken() {
        if (!this.csrfToken) await this.getSession();
        return this.csrfToken;
    }

    static async requestJson(url, options = {}, requiresCsrf = false) {
        const headers = { ...(options.headers || {}) };
        if (options.body && !headers['Content-Type']) headers['Content-Type'] = 'application/json';
        if (requiresCsrf) headers['X-CSRF-TOKEN'] = (await this.getCsrfToken()) || '';
        const response = await fetch(url, { credentials: 'same-origin', ...options, headers });
        const text = await response.text();
        let body = {};
        if (text) {
            try { body = JSON.parse(text); }
            catch { body = { error: text }; }
        }
        if (!response.ok && !body.error && !body.errors) body.error = body.title || `Request failed (${response.status})`;
        return body;
    }

    static deleteUser(username) {
        return this.requestJson(`/api/particle-api/admin/customers/${encodeURIComponent(username)}`, { method: 'DELETE' }, true);
    }

    static resendWelcomeEmail(username) {
        return this.requestJson('/api/Particle/MargoResendWelcome', {
            method: 'POST', body: JSON.stringify({ username, sendingUser: localStorage.getItem('currentUser') })
        }, true);
    }

    static async makeUser(username, password) {
        const result = await this.requestJson(`/api/particle-api/admin/products/${LTEProductID}/customers`, {
            method: 'POST', body: JSON.stringify({ email: username, password })
        }, true);
        // This preserves the existing UI contract; the value is an opaque short-lived grant, not a Particle token.
        if (result.provisioningGrantId) result.access_token = result.provisioningGrantId;
        return result;
    }

    static async getProductCustomers(productId, productName) {
        const rows = [];
        let page = 1;
        let pages = 1;
        do {
            const result = await this.requestJson(`/api/particle-api/admin/products/${productId}/customers?page=${page}`);
            if (result.error) return rows;
            pages = result.meta?.total_pages || 1;
            (result.customers || []).forEach(customer => rows.push({ ...customer, product: productName }));
            page += 1;
        } while (page <= pages);
        return rows;
    }

    static getLTEUsers() { return this.getProductCustomers(LTEProductID, "LTE"); }
    static getWIFIUsers() { return this.getProductCustomers(WIFIProductID, "WiFi"); }

    static async getDeviceID(serialNum) {
        const result = await this.requestJson(`/api/particle-api/serial/${encodeURIComponent(serialNum)}`);
        return result.device_id;
    }

    static async getDevices(id = null) {
        if (id) return this.getDevice(id);
        const result = await this.requestJson('/api/particle-api/devices');
        if (!Array.isArray(result)) return [];
        return result.filter(device => device.product_id === LTEProductID || device.product_id === WIFIProductID);
    }

    static productDevicePath(productId, id) {
        return `/api/particle-api/admin/products/${encodeURIComponent(productId)}/devices/${encodeURIComponent(id)}`;
    }

    static getDevice(id = null, productId = null) {
        if (id && productId) return this.requestJson(this.productDevicePath(productId, id));
        return this.requestJson(id ? `/api/particle-api/devices/${encodeURIComponent(id)}` : '/api/particle-api/devices');
    }

    static async getAdminFleetDevices() {
        if (!this.adminFleetPromise || Date.now() - this.adminFleetLoadedAt > 5000) {
            this.adminFleetLoadedAt = Date.now();
            this.adminFleetPromise = this.requestJson('/api/particle-api/admin/fleet/devices');
        }
        const result = await this.adminFleetPromise;
        return Array.isArray(result) ? result : [];
    }

    static async getAdminFleetLocations() {
        const results = await Promise.all([LTEProductID, WIFIProductID].map(productId =>
            this.requestJson(`/api/particle-api/admin/products/${encodeURIComponent(productId)}/fleet-locations`)));
        return results.flatMap(result => Array.isArray(result?.locations) ? result.locations : []);
    }

    static async getProductDevices() {
        const devices = [];
        for (const productId of [LTEProductID, WIFIProductID]) {
            let page = 1;
            let pages = 1;
            do {
                const result = await this.requestJson(`/api/particle-api/admin/products/${productId}/devices?page=${page}`);
                pages = result.meta?.total_pages || 1;
                (result.devices || []).forEach(device => devices.push(device));
                page += 1;
            } while (page <= pages);
        }
        return devices;
    }

    static claimDevice(id) {
        return this.requestJson('/api/particle-api/devices/claim', { method: 'POST', body: JSON.stringify({ id }) }, true);
    }

    static addDeviceUser(provisioningGrantId, deviceId) {
        return this.requestJson(`/api/particle-api/admin/provisioning/${encodeURIComponent(provisioningGrantId)}/devices/${encodeURIComponent(deviceId)}/claim`, { method: 'POST' }, true);
    }

    static assignProductDevice(productId, deviceId, customer, name, confirmTransfer = false) {
        return this.requestJson(
            `/api/particle-api/admin/products/${encodeURIComponent(productId)}/devices/${encodeURIComponent(deviceId)}/assign`,
            {
                method: 'POST',
                body: JSON.stringify({ customer, name, confirmTransfer }),
            },
            true
        );
    }

    static unclaimProductDevice(productId, deviceId) {
        return this.requestJson(
            `/api/particle-api/admin/products/${encodeURIComponent(productId)}/devices/${encodeURIComponent(deviceId)}/owner`,
            { method: 'DELETE' },
            true
        );
    }

    static renameDevice(id, newName, provisioningGrantId = null) {
        const url = provisioningGrantId
            ? `/api/particle-api/admin/provisioning/${encodeURIComponent(provisioningGrantId)}/devices/${encodeURIComponent(id)}`
            : `/api/particle-api/devices/${encodeURIComponent(id)}`;
        return this.requestJson(url, { method: 'PUT', body: JSON.stringify({ name: newName }) }, true);
    }

    static renameFleetDevice(id, productId, newName) {
        return this.requestJson(this.productDevicePath(productId, id), {
            method: 'PUT', body: JSON.stringify({ name: newName })
        }, true);
    }

    static async Login(username, password) {
        const result = await this.requestJson('/api/Particle/particle-login', {
            method: 'POST', body: JSON.stringify({ username, password })
        });
        if (result.authenticated) {
            localStorage.removeItem('my-token');
            localStorage.setItem('currentUser', username);
            this.csrfToken = null;
            return true;
        }
        console.error(result);
        return false;
    }

    static async logout() {
        try {
            const csrfToken = await ParticleService.getCsrfToken();
            await fetch('/api/auth/logout', {
                method: 'POST', credentials: 'same-origin',
                headers: { 'X-CSRF-TOKEN': csrfToken || '' }
            });
        } finally {
            ParticleService.csrfToken = null;
            localStorage.removeItem('my-token');
            localStorage.removeItem('currentUser');
            window.location.reload(false);
        }
    }

    static requestResetPassword(username) {
        return fetch('/api/Particle/RequestResetEmail', {
            method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ username })
        });
    }

    static changePassword(username, password, securityCode) {
        return fetch('/api/Particle/RequestParticleResetPassword', {
            method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ username, password, securityCode })
        });
    }

    static getUserToken() { return null; }
    static checkName(nameCheck) { return new RegExp('^[a-zA-Z0-9+_-]+$').test(nameCheck); }

    static async getDeviceNotes(id, productId = null) { return (await this.getDevice(id, productId)).notes; }
    static setDeviceNotes(id, notes, productId = null) {
        const url = productId ? this.productDevicePath(productId, id) : `/api/particle-api/devices/${encodeURIComponent(id)}`;
        return this.requestJson(url, { method: 'PUT', body: JSON.stringify({ notes }) }, true);
    }
    static signalDevice(id, signal, productId = null) {
        const url = productId ? this.productDevicePath(productId, id) : `/api/particle-api/devices/${encodeURIComponent(id)}`;
        return this.requestJson(url, { method: 'PUT', body: JSON.stringify({ signal }) }, true);
    }
    static callFunction(id, functionName, arg = "0", productId = null) {
        const url = productId
            ? `${this.productDevicePath(productId, id)}/functions/${encodeURIComponent(functionName)}`
            : `/api/particle-api/devices/${encodeURIComponent(id)}/functions/${encodeURIComponent(functionName)}`;
        return this.requestJson(url, { method: 'POST', body: JSON.stringify({ arg }) }, true);
    }
    static fireCannon(id, productId = null) { return this.callFunction(id, 'fireCannon', '0', productId); }
    static resetTank(id, productId = null) { return this.callFunction(id, 'resetTank', '0', productId); }
    static setCannonSettings(id, settings, productId = null) { return this.callFunction(id, 'setCannonSettings', settings, productId); }
    static variablePath(id, variableName, productId = null) {
        return productId
            ? `${this.productDevicePath(productId, id)}/variables/${encodeURIComponent(variableName)}`
            : `/api/particle-api/devices/${encodeURIComponent(id)}/variables/${encodeURIComponent(variableName)}`;
    }
    static getCannonStats(id, productId = null) { return this.requestJson(this.variablePath(id, 'stats', productId)); }
    static fetchVitals(id) { return this.requestJson(`/api/particle-api/diagnostics/${encodeURIComponent(id)}`); }
    static getCannonSettings(id, productId = null) { return this.requestJson(this.variablePath(id, 'settings', productId)); }
    static getCannonLocation(id, productId = null) { return this.requestJson(this.variablePath(id, 'locationCallback', productId)); }
    static manageSim(iccid, action) {
        return this.requestJson('/api/Particle/manage-sim', { method: 'POST', body: JSON.stringify({ iccid, action }) }, true);
    }
    static async getSim(iccid) {
        if (!iccid) return null;
        const result = await this.requestJson(`/api/particle-api/admin/sims/${encodeURIComponent(iccid)}`);
        return result.error ? null : result;
    }

    static getCustomerRegistry(search = '', page = 1, options = {}) {
        const query = new URLSearchParams({
            search,
            page: String(page),
            pageSize: '50',
            accountStatus: options.accountStatus || '',
            accessStatus: options.accessStatus || '',
            deviceCoverage: options.deviceCoverage || '',
            salesStatus: options.salesStatus || '',
            sortBy: options.sortBy || 'customer',
            sortDirection: options.sortDirection || 'asc',
        });
        return this.requestJson(`/api/admin/customers?${query}`);
    }

    static getCustomerProfile(userId) {
        return this.requestJson(`/api/admin/customers/${encodeURIComponent(userId)}`);
    }

    static enforceCustomerAccess(userId, deviceId, reason, confirmation) {
        return this.requestJson(`/api/admin/customers/${encodeURIComponent(userId)}/devices/${encodeURIComponent(deviceId)}/enforce`, {
            method: 'POST', body: JSON.stringify({ reason, confirmation })
        }, true);
    }

    static restoreCustomerAccess(userId, deviceId, body) {
        return this.requestJson(`/api/admin/customers/${encodeURIComponent(userId)}/devices/${encodeURIComponent(deviceId)}/restore`, {
            method: 'POST', body: JSON.stringify(body)
        }, true);
    }

    static updateCustomerEntitlement(userId, subscriptionId, body) {
        return this.requestJson(`/api/admin/customers/${encodeURIComponent(userId)}/subscriptions/${encodeURIComponent(subscriptionId)}`, {
            method: 'PUT', body: JSON.stringify(body)
        }, true);
    }

    static cancelCustomerSubscription(userId, subscriptionId, reason) {
        return this.requestJson(`/api/admin/customers/${encodeURIComponent(userId)}/subscriptions/${encodeURIComponent(subscriptionId)}/cancel-renewal`, {
            method: 'POST', body: JSON.stringify({ reason })
        }, true);
    }

    static grantComplimentaryEntitlement(userId, deviceId, body) {
        return this.requestJson(`/api/admin/customers/${encodeURIComponent(userId)}/devices/${encodeURIComponent(deviceId)}/complimentary-entitlement`, {
            method: 'POST', body: JSON.stringify(body)
        }, true);
    }

    static createCustomerDiscount(userId, body) {
        return this.requestJson(`/api/admin/customers/${encodeURIComponent(userId)}/discounts`, {
            method: 'POST', body: JSON.stringify(body)
        }, true);
    }

    static updateCustomerDiscount(userId, discountId, body) {
        return this.requestJson(`/api/admin/customers/${encodeURIComponent(userId)}/discounts/${encodeURIComponent(discountId)}`, {
            method: 'PUT', body: JSON.stringify(body)
        }, true);
    }

    static endCustomerDiscount(userId, discountId, reason) {
        return this.requestJson(`/api/admin/customers/${encodeURIComponent(userId)}/discounts/${encodeURIComponent(discountId)}/end`, {
            method: 'POST', body: JSON.stringify({ reason })
        }, true);
    }

    static synchronizeCustomerRegistry() {
        return this.requestJson('/api/admin/customers/sync-particle', { method: 'POST' }, true);
    }

    static getSubscriptionSettings() {
        return this.requestJson('/api/admin/settings/subscriptions');
    }

    static getIntegrationReadiness() {
        return this.requestJson('/api/admin/settings/integrations');
    }

    static sendOperationalAlertTest() {
        return this.requestJson('/api/admin/settings/operational-alerts/send-test', { method: 'POST' }, true);
    }

    static getWooCommerceProductMappings() {
        return this.requestJson('/api/admin/settings/woocommerce-products');
    }

    static createWooCommerceProductMapping(body) {
        return this.requestJson('/api/admin/settings/woocommerce-products', {
            method: 'POST', body: JSON.stringify(body)
        }, true);
    }

    static updateWooCommerceProductMapping(id, body) {
        return this.requestJson(`/api/admin/settings/woocommerce-products/${encodeURIComponent(id)}`, {
            method: 'PUT', body: JSON.stringify(body)
        }, true);
    }

    static getPurchaseFulfillmentQueue(search = '', status = '', store = '') {
        const query = new URLSearchParams({ search, status, store });
        return this.requestJson(`/api/admin/fulfillment?${query}`);
    }

    static fulfillPurchase(purchaseId, body) {
        return this.requestJson(`/api/admin/fulfillment/purchases/${encodeURIComponent(purchaseId)}/devices`, {
            method: 'POST', body: JSON.stringify({ ...body, reason: body.reason.trim() })
        }, true);
    }

    static reversePurchaseFulfillment(fulfillmentId, reason) {
        return this.requestJson(`/api/admin/fulfillment/devices/${encodeURIComponent(fulfillmentId)}/reverse`, {
            method: 'POST', body: JSON.stringify({ reason })
        }, true);
    }

    static reconcilePurchaseAllocation(purchaseId, reason) {
        return this.requestJson(`/api/admin/fulfillment/purchases/${encodeURIComponent(purchaseId)}/reconcile`, {
            method: 'POST', body: JSON.stringify({ reason })
        }, true);
    }

    static completePurchaseReview(purchaseId, disposition, resolution) {
        return this.requestJson(`/api/admin/fulfillment/purchases/${encodeURIComponent(purchaseId)}/complete-review`, {
            method: 'POST', body: JSON.stringify({ disposition, resolution })
        }, true);
    }

    static getPaymentOperations(options = {}) {
        const query = new URLSearchParams({
            search: options.search || '',
            reviewStatus: options.reviewStatus || 'needs-review',
            provider: options.provider || '',
            transactionStatus: options.transactionStatus || '',
            transactionType: options.transactionType || '',
            page: String(options.page || 1),
            pageSize: String(options.pageSize || 50),
        });
        return this.requestJson(`/api/admin/payment-operations?${query}`);
    }

    static completePaymentReview(transactionId, disposition, resolution) {
        return this.requestJson(`/api/admin/payment-operations/transactions/${encodeURIComponent(transactionId)}/complete-review`, {
            method: 'POST', body: JSON.stringify({ disposition, resolution })
        }, true);
    }

    static replayFailedWebhook(inboxId, reason) {
        return this.requestJson(`/api/admin/payment-operations/webhooks/${encodeURIComponent(inboxId)}/replay`, {
            method: 'POST', body: JSON.stringify({ reason })
        }, true);
    }

    static previewWooCommerceOrder(store, orderId) {
        return this.requestJson(`/api/admin/payment-operations/woocommerce/${encodeURIComponent(store)}/orders/${encodeURIComponent(orderId)}/preview`);
    }

    static importWooCommerceOrder(store, orderId, reason, expectedVersionToken) {
        return this.requestJson(`/api/admin/payment-operations/woocommerce/${encodeURIComponent(store)}/orders/${encodeURIComponent(orderId)}/import`, {
            method: 'POST', body: JSON.stringify({ reason, expectedVersionToken })
        }, true);
    }

    static updateSubscriptionSettings(body) {
        return this.requestJson('/api/admin/settings/subscriptions', {
            method: 'PUT', body: JSON.stringify(body)
        }, true);
    }

    static runSubscriptionMaintenance() {
        return this.requestJson('/api/admin/settings/subscriptions/run-maintenance', {
            method: 'POST'
        }, true);
    }

    static adoptHelcimSubscription(checkoutSessionId, externalSubscriptionId) {
        return this.requestJson('/api/admin/settings/subscriptions/adopt-helcim', {
            method: 'POST', body: JSON.stringify({ checkoutSessionId, externalSubscriptionId: Number(externalSubscriptionId) })
        }, true);
    }

    static getMySubscriptionStatus() {
        return this.requestJson('/api/subscriptions/me');
    }

    static getCheckoutOffers() {
        return this.requestJson('/api/subscriptions/me/checkout/offers');
    }

    static startHelcimCheckout(currency, billingInterval, deviceId) {
        return this.requestJson('/api/subscriptions/me/checkout', {
            method: 'POST', body: JSON.stringify({ currency, billingInterval, deviceId })
        }, true);
    }

    static completeHelcimCheckout(sessionId, response) {
        return this.requestJson(`/api/subscriptions/me/checkout/${encodeURIComponent(sessionId)}/complete`, {
            method: 'POST', body: JSON.stringify({ hash: response.hash, data: response.data })
        }, true);
    }

    static activateIncludedFreePeriod(deviceId) {
        return this.requestJson(`/api/subscriptions/me/devices/${encodeURIComponent(deviceId)}/activate-free-period`, {
            method: 'POST'
        }, true);
    }

    static getLegacyImportStatus() {
        return this.requestJson('/api/admin/customers/legacy-import');
    }

    static stageLegacyWorkbook() {
        return this.requestJson('/api/admin/customers/legacy-import/stage', { method: 'POST' }, true);
    }

    static reconcileLegacyWorkbook() {
        return this.requestJson('/api/admin/customers/legacy-import/reconcile', { method: 'POST' }, true);
    }

    static applyReviewedLegacyWorkbook() {
        return this.requestJson('/api/admin/customers/legacy-import/apply', { method: 'POST' }, true);
    }
}

export default ParticleService;
